/**
 * Flagi oferty „Sprawdź ofertę”: co trzeba wyjaśnić przed podpisaniem umowy.
 *
 * ═══ TRZY POZIOMY (decyzja właściciela 2026-09-29) ═══
 * - `czerwona`: twardy fakt, który sprawia, że instalacja nie zadziała albo narusza
 *   granice urządzenia. Tylko z oficjalnego źródła albo z fizyki przy DOKŁADNIE
 *   znanych modelach. Fałszywa czerwona flaga podważa całe narzędzie, więc gdy
 *   wniosek zależy od tego, który model zamontuje instalator, flaga jest pomarańczowa.
 * - `pomaranczowa`: do wyjaśnienia z instalatorem. Nie wyrok, tylko pytanie.
 * - `info`: na co zwrócić uwagę przy montażu (np. podział modułów na łańcuchy).
 *
 * ═══ TREŚĆ FLAGI (przebudowa 2026-09-30, właściciel: „tekstu za dużo”) ═══
 * `tytul` mówi, co jest nie tak, `akcja` jednym zdaniem, co zrobić, `fakt` (opcjonalnie)
 * to liczby w jednej linijce, a `dlaczego` jest schowane pod „Dlaczego?”. `skala` rysuje
 * małą skalę (moc falownika wobec pasującego zakresu).
 *
 * Czyste funkcje: dane z `POST /api/sprawdz_oferte/` i raportu z `SprawdzOferte.jsx`.
 */
import { ocenaSerwisu, serwisMagazynu } from './szacunekOferty'
import { pojemnoscMagazynu } from './pme2'
import { ARTYKUL_PPOZ, wymagaPpoz } from '../config/ppoz'

// Przewymiarowanie mocy modułów względem falownika (DC/AC). Skrajne przypadki są czerwone
// (decyzja właściciela 2026-09-29): moduły ponad 2 razy mocniejsze niż falownik albo falownik
// tak duży, że moduły dają mniej niż 0,6 jego mocy.
export const DCAC_WYSOKI = 1.5 // powyżej falownik wyraźnie ucina szczyty produkcji
export const DCAC_NISKI = 0.8 // poniżej falownik jest większy, niż potrzeba
export const DCAC_SKRAJNIE_WYSOKI = 2
export const DCAC_SKRAJNIE_NISKI = 0.6
// Falownik jednofazowy przy większej instalacji (decyzja właściciela 2026-09-29).
export const JEDNOFAZOWY_MAKS_KWP = 4
// Cena tak daleko od typowego zakresu, że warto zapytać o powód.
export const CENA_DALEKO_PROC = 30

const liczba = (v, miejsc = 1) => String(Math.round(v * 10 ** miejsc) / 10 ** miejsc).replace('.', ',')
const kandydaci = (sekcja) => sekcja?.kandydaci || []
const dokladny = (sekcja) => kandydaci(sekcja).length === 1

/** Wspólna wartość pola u wszystkich kandydatów albo null, gdy się różnią lub jej brak. */
const wspolne = (sekcja, pole) => {
  const w = new Set(kandydaci(sekcja).map((k) => k[pole]).filter((v) => v != null && v !== ''))
  return w.size === 1 && kandydaci(sekcja).every((k) => k[pole] != null) ? [...w][0] : null
}

/** „11 i 10”, „2 × 8” albo „2 × 9 i 8” z podziału [[n, łańcuchów], ...]. */
export const opisPodzialu = (podzial) => (podzial || [])
  .map(([n, s]) => (s > 1 ? `${s} × ${n}` : String(n)))
  .join(' i ')

const lancuchyWord = (k) => (k === 1 ? 'łańcuch' : k < 5 ? 'łańcuchy' : 'łańcuchów')

// ═══ FALOWNIK Z MAGAZYNEM ═══
const flagiZgodnosci = (wynik) => {
  const para = wynik?.zgodnosc?.para
  if (!para) return []
  if (para.status === 'incompatible') {
    const powod = {
      closed_ecosystem: {
        fakt: 'Producent falownika dopuszcza tylko magazyny własnej marki.',
        akcja: 'Poproś o magazyn tej samej marki co falownik albo o inny falownik.',
        dlaczego: 'To zamknięty system: falownik współpracuje wyłącznie z bateriami swojego producenta. Źródło jest niżej, w sekcji o współpracy falownika z magazynem.',
      },
      voltage_class_mismatch: {
        fakt: 'Magazyn i falownik pracują na różnych napięciach.',
        akcja: 'Poproś o magazyn o napięciu zgodnym z falownikiem.',
        dlaczego: 'Magazyn wysokonapięciowy działa tylko z falownikiem wysokonapięciowym, a niskonapięciowy z niskonapięciowym. Takiej pary nie da się połączyć.',
      },
      inverter_has_no_dc_battery_port: {
        fakt: 'To falownik sieciowy, bez wejścia na baterię.',
        akcja: 'Poproś o falownik hybrydowy albo osobny falownik bateryjny.',
        dlaczego: 'Magazyn bez własnego falownika ładuje się przez falownik z wejściem na baterię. W takim zestawie magazyn nie będzie działać.',
      },
    }[para.reason_code] || {
      fakt: 'Według dokumentacji producenta ta para nie współpracuje.',
      akcja: 'Poproś o zestaw, który potwierdza producent.',
    }
    return [{ poziom: 'czerwona', kod: 'niezgodne', tytul: 'Falownik i magazyn nie współpracują', ...powod }]
  }
  if (para.status === 'rozne' && para.statusy?.incompatible) {
    return [{ poziom: 'pomaranczowa', kod: 'niezgodne_czesc', tytul: 'Zgodność zależy od dokładnego modelu falownika',
      akcja: 'Zapytaj o pełną nazwę modelu i potwierdzenie producenta.',
      dlaczego: 'Opis falownika w ofercie pasuje do kilku modeli, a część z nich nie współpracuje z tym magazynem.' }]
  }
  if (para.status === 'conflicting') {
    return [{ poziom: 'pomaranczowa', kod: 'zgodnosc_sprzeczna', tytul: 'Sprzeczne dane o współpracy falownika z magazynem',
      akcja: 'Poproś o dokument producenta, który potwierdza tę parę.',
      dlaczego: 'Dokumenty producentów, które analizujemy, mówią o tej parze co innego.' }]
  }
  if (para.status === 'unknown' || para.status === 'rozne') {
    return [{ poziom: 'pomaranczowa', kod: 'zgodnosc_brak', tytul: 'Brak potwierdzenia, że falownik współpracuje z magazynem',
      akcja: 'Poproś o pisemne potwierdzenie współpracy.',
      dlaczego: 'To nie znaczy, że nie zadziała. Tej pary nie ma w dokumentach producentów, które analizujemy.' }]
  }
  return []
}

/**
 * Liczba modułów magazynu wobec limitów z reguły zgodności. Tylko gdy wszystkie pasujące
 * modele mają tę samą pojemność i składają się z całej liczby modułów z reguły:
 * inaczej nie wiemy, ile modułów naprawdę jest w ofercie (np. Felicity FLA48460 23,5 kWh
 * w rodzinie modułów 15 kWh).
 */
const flagiModulowMagazynu = (wynik, wejscie) => {
  const para = wynik?.zgodnosc?.para
  const c = para?.pojemnosc
  if (!c || !['compatible', 'conditional'].includes(para.status)) return []
  const sztuk = wejscie?.liczbaMagazynu > 1 ? wejscie.liczbaMagazynu : 1
  const jednostka = pojemnoscMagazynu(null, kandydaci(wynik.magazyn))
  if (!jednostka) return []
  const razem = jednostka * sztuk
  const flagi = []
  const m = c.module_capacity_kwh
  if (m) {
    const ile = jednostka / m
    if (Math.abs(ile - Math.round(ile)) <= 0.05) {
      const modulow = Math.round(ile) * sztuk
      const maks = c.max_modules_per_tower ? c.max_modules_per_tower * (c.max_parallel_towers || 1) : null
      if (c.min_modules && modulow < c.min_modules) {
        flagi.push({ poziom: 'czerwona', kod: 'magazyn_za_malo_modulow', tytul: 'Za mało modułów magazynu dla tego falownika',
          fakt: `Moduły po ${liczba(m, 2)} kWh: wymagane co najmniej ${c.min_modules}, w ofercie ${modulow}.`,
          akcja: `Poproś o magazyn z co najmniej ${c.min_modules} modułami.`,
          dlaczego: 'Przy mniejszej liczbie modułów magazyn nie osiągnie napięcia, na którym pracuje z falownikiem.' })
      } else if (maks && modulow > maks) {
        flagi.push({ poziom: 'czerwona', kod: 'magazyn_za_duzo_modulow', tytul: 'Więcej modułów magazynu, niż dopuszcza producent',
          fakt: `Moduły po ${liczba(m, 2)} kWh: z tym falownikiem najwyżej ${maks}, w ofercie ${modulow}.`,
          akcja: 'Zapytaj, jak instalator je podłączy, i poproś o potwierdzenie producenta.' })
      }
    }
  }
  if (!flagi.length && c.max_total_capacity_kwh && razem > c.max_total_capacity_kwh + 0.01) {
    flagi.push({ poziom: 'czerwona', kod: 'magazyn_ponad_limit', tytul: 'Pojemność magazynu ponad limit producenta',
      fakt: `Z tym falownikiem łącznie do ${liczba(c.max_total_capacity_kwh, 2)} kWh, w ofercie ${liczba(razem, 2)} kWh.`,
      akcja: 'Zapytaj, jak instalator go podłączy, i poproś o potwierdzenie producenta.' })
  }
  return flagi
}

// ═══ MODUŁY Z FALOWNIKIEM ═══
/**
 * Łańcuchy liczy backend (`electrical/stringi.sprawdz_liczbe`) dla KAŻDEGO pasującego
 * falownika, przy najkorzystniejszym podziale modułów. Flagę zapalamy tylko wtedy,
 * gdy problem dotyczy wszystkich pasujących falowników. Czerwoną tylko przy jednym
 * znanym falowniku i jednym znanym module.
 */
const flagiLancuchow = (wynik, wejscie) => {
  const wiersze = (wynik?.stringi || []).filter((s) => s.ok && s.lancuchy)
  if (!wiersze.length) return []
  const wszystkie = (pred) => wiersze.every((s) => pred(s.lancuchy))
  const pewne = wiersze.length === 1 && dokladny(wynik.falownik) && dokladny(wynik.moduly)
  const l = wiersze[0].lancuchy
  const n = wejscie?.liczbaModulow
  const flagi = []

  if (wszystkie((x) => x.prad === 'zwarciowy')) {
    flagi.push({ poziom: pewne ? 'czerwona' : 'pomaranczowa', kod: 'prad_zwarciowy',
      tytul: 'Za duży prąd modułów dla wejścia falownika',
      fakt: `Moduł w upale do ${liczba(l.isc_modulu_upal_a)} A, wejście falownika do ${liczba(l.isc_wejscia_a)} A.`,
      akcja: 'Poproś o inny falownik albo inne moduły.',
      dlaczego: 'To granica bezpieczeństwa wejścia falownika. Nie zmieni jej inny sposób połączenia modułów.' })
  } else if (wszystkie((x) => x.prad === 'roboczy')) {
    flagi.push({ poziom: 'pomaranczowa', kod: 'prad_roboczy', tytul: 'Falownik nie wykorzysta pełnego prądu modułów',
      fakt: `Moduł ${liczba(l.prad_modulu_a)} A, wejście falownika ${liczba(l.prad_wejscia_a)} A: w pełnym słońcu utnie ok. ${l.strata_proc}% mocy.`,
      akcja: 'Zapytaj, czy instalator wziął to pod uwagę.',
      dlaczego: 'Instalacja zadziała, ale w najlepszych godzinach falownik ograniczy prąd modułów i część energii przepadnie.' })
  }

  if (wszystkie((x) => x.napiecie === 'za_duzo')) {
    flagi.push({ poziom: pewne && l.uklad_wejsc_znany ? 'czerwona' : 'pomaranczowa', kod: 'za_duzo_modulow',
      tytul: 'Tylu modułów nie da się podłączyć do tego falownika',
      fakt: `${n} modułów w każdym układzie łańcuchów przekroczy w mrozie dopuszczalne napięcie falownika${l.napiecie_dc_max_v ? ` (${Math.round(l.napiecie_dc_max_v)} V)` : ''}.`,
      akcja: 'Poproś o schemat połączeń albo drugi falownik.',
      dlaczego: 'Zimą napięcie modułów rośnie. Powyżej dopuszczalnego napięcia wejście falownika może się uszkodzić.' })
  } else if (wszystkie((x) => x.napiecie === 'poza_oknem')) {
    const zakres = l.n_min_mppt && l.n_max_mppt
      ? `Łańcuch powinien mieć od ${l.n_min_mppt} do ${Math.min(l.n_max_mppt, l.n_max_dc)} modułów.` : null
    flagi.push({ poziom: 'pomaranczowa', kod: 'poza_oknem_mppt', tytul: 'Łańcuchy poza zakresem pracy falownika',
      fakt: zakres,
      akcja: 'Poproś o schemat połączeń.',
      dlaczego: `Przy ${n} modułach łańcuchy nie zmieszczą się w zakresie napięć, w którym falownik pracuje najwydajniej. Instalacja zadziała, ale może produkować mniej.` })
  } else if (wszystkie((x) => x.napiecie === 'ok' && x.jeden_lancuch !== 'ok' && x.lancuchow_min >= 2)) {
    const k = l.lancuchow_min
    const powod = l.jeden_lancuch === 'za_duzo'
      ? 'przekroczyłyby dopuszczalne napięcie falownika w mrozie'
      : 'wyszłyby poza zakres pracy falownika'
    flagi.push({ poziom: 'info', kod: 'podzial_lancuchow', tytul: `Moduły trzeba podzielić na co najmniej ${k} ${lancuchyWord(k)}`,
      akcja: `Dopilnuj podziału między wejścia falownika, np. ${opisPodzialu(l.podzial)} modułów.`,
      dlaczego: `Wszystkie ${n} modułów w jednym łańcuchu ${powod}.` })
  }
  return flagi
}

/** Moc modułów wobec mocy falownika. Moc falownika tylko wtedy, gdy wszyscy kandydaci ją mają tę samą. */
const flagiMocy = (wynik, kwp) => {
  const moc = wspolne(wynik?.falownik, 'moc_kw')
  const flagi = []
  if (kwp && moc) {
    const dcac = kwp / moc
    // Moc falownika, przy której flagi nie ma: moduły od 0,8 do 1,5 jego mocy.
    const skala = (poziom) => ({
      od: kwp / DCAC_WYSOKI, do: kwp / DCAC_NISKI, wartosc: moc, poziom,
      etykietaZakresu: `pasuje ${liczba(kwp / DCAC_WYSOKI)} do ${liczba(kwp / DCAC_NISKI)} kW`,
      etykietaWartosci: `w ofercie ${liczba(moc, 2)} kW`,
    })
    const fakt = `Moduły ${liczba(kwp, 2)} kWp, falownik ${liczba(moc, 2)} kW.`
    if (dcac > DCAC_SKRAJNIE_WYSOKI) {
      flagi.push({ poziom: 'czerwona', kod: 'falownik_za_maly', tytul: 'Falownik o wiele za mały do tych modułów', fakt, skala: skala('czerwona'),
        akcja: 'Poproś o większy falownik albo o drugi.',
        dlaczego: 'Moduły mają ponad dwa razy większą moc niż falownik. Przez dużą część dnia falownik będzie ucinał produkcję i stracisz sporą część energii.' })
    } else if (dcac > DCAC_WYSOKI) {
      flagi.push({ poziom: 'pomaranczowa', kod: 'falownik_za_maly', tytul: 'Falownik mały wobec mocy modułów', fakt, skala: skala('pomaranczowa'),
        akcja: 'Zapytaj, czy to świadomy wybór.',
        dlaczego: `Moduły mają o ${Math.round((dcac - 1) * 100)}% większą moc niż falownik. W słoneczne dni falownik będzie ucinał szczyty produkcji.` })
    } else if (dcac < DCAC_SKRAJNIE_NISKI) {
      flagi.push({ poziom: 'czerwona', kod: 'falownik_za_duzy', tytul: 'Falownik o wiele za duży do tych modułów', fakt, skala: skala('czerwona'),
        akcja: 'Poproś o wycenę z mniejszym falownikiem.',
        dlaczego: 'Moduły dają mniej niż 60% mocy falownika. Płacisz za moc, której nie wykorzystasz, a przy małym obciążeniu falownik pracuje mniej sprawnie.' })
    } else if (dcac < DCAC_NISKI) {
      flagi.push({ poziom: 'pomaranczowa', kod: 'falownik_za_duzy', tytul: 'Falownik większy, niż wymagają moduły', fakt, skala: skala('pomaranczowa'),
        akcja: 'Zapytaj, po co taki zapas mocy.',
        dlaczego: 'Zapas ma sens przy planowanej rozbudowie albo dużym magazynie z zasilaniem awaryjnym. Inaczej płacisz za moc, której nie wykorzystasz.' })
    }
  }
  if (kwp > JEDNOFAZOWY_MAKS_KWP && wspolne(wynik?.falownik, 'fazy') === 1) {
    flagi.push({ poziom: 'pomaranczowa', kod: 'jednofazowy', tytul: 'Falownik jednofazowy przy instalacji powyżej 4 kWp',
      akcja: 'Zapytaj, dlaczego nie trójfazowy i czy operator sieci zgodzi się na takie przyłączenie.',
      dlaczego: `Przy ${liczba(kwp, 2)} kWp zwykle stosuje się falownik trójfazowy. Jednofazowy oddaje całą moc na jedną fazę, więc łatwiej podnosi napięcie w sieci i częściej się wtedy wyłącza.` })
  }
  return flagi
}

/**
 * Ponad 6,5 kWp: uzgodnienie projektu z rzeczoznawcą ppoż i zawiadomienie PSP (art. 29 ust. 4
 * pkt 3 lit. c Prawa budowlanego). Poziom `info`, a nie pomarańczowy: to formalność, a nie wada
 * oferty, i dotyczy większości instalacji, więc nie może wyglądać jak problem z ofertą. Pytanie
 * do instalatora zostaje, bo uzgodnienie bywa pomijane w cenie (prośba właściciela 2026-10-09).
 */
const flagiFormalnosci = (kwp) => (wymagaPpoz(kwp) ? [{
  poziom: 'info', kod: 'ppoz_psp', tytul: 'Ponad 6,5 kWp: uzgodnienie ppoż i zawiadomienie straży pożarnej',
  fakt: `Moduły ${liczba(kwp, 2)} kWp.`,
  akcja: 'Zapytaj, czy uzgodnienie projektu z rzeczoznawcą jest w cenie i kto przygotuje zawiadomienie do PSP.',
  dlaczego: 'Przed montażem projekt musi uzgodnić rzeczoznawca do spraw zabezpieczeń przeciwpożarowych, a po montażu trzeba zawiadomić komendę PSP i przekazać plan instalacji dla ekip ratowniczych. Obowiązek ma inwestor, ale zwykle załatwia go instalator.',
  link: ARTYKUL_PPOZ,
}] : [])

/**
 * Magazyn przy falowniku sieciowym to zawsze czerwona flaga (decyzja właściciela 2026-09-29):
 * falownik sieciowy nie ma wejścia na baterię. Wyjątki tylko wtedy, gdy dane pokazują, że to
 * NIE jest prawdziwy falownik sieciowy albo magazyn go nie potrzebuje:
 * - katalog opisuje część hybryd jako sieciowe (Huawei SUN2000-M1, Fronius GEN24 „sieciowy
 *   hybrydowy”): „hybryd” w nazwie, falownik w grupie bazy zgodności albo potwierdzona para
 *   znaczą, że falownik obsługuje baterię, więc flagi nie ma;
 * - magazyn z własnym falownikiem (All-In-One, Enphase) podłącza się po stronie prądu
 *   przemiennego, więc zostaje pytanie, a nie czerwona flaga.
 */
const TYPY_BEZ_BATERII = ['Sieciowy', 'Mikroinwertery']
const TYPY_Z_FALOWNIKIEM = ['All-In-One']

export const falownikBezBaterii = (wynik) => {
  const f = kandydaci(wynik?.falownik)
  if (!f.length || !f.every((k) => TYPY_BEZ_BATERII.includes(k.typ))) return null
  const para = wynik?.zgodnosc?.para
  const hybrydaWKatalogu = f.some((k) => /hybryd/i.test(k.nazwa || ''))
    || para?.falownik_w_grupie === true
    || (para && ['compatible', 'conditional'].includes(para.status))
  if (hybrydaWKatalogu) return null
  const m = kandydaci(wynik?.magazyn)
  const zWlasnymFalownikiem = m.length > 0
    && m.every((k) => TYPY_Z_FALOWNIKIEM.includes(k.typ) || /enphase/i.test(k.producent || ''))
  return zWlasnymFalownikiem ? 'pomaranczowa' : 'czerwona'
}

const flagiSprzetu = (wynik, wejscie) => {
  const flagi = []
  const magazyn = wejscie?.magazyn && !wejscie.magazyn.brak
  const bezBaterii = magazyn ? falownikBezBaterii(wynik) : null
  if (bezBaterii === 'czerwona') {
    flagi.push({ poziom: 'czerwona', kod: 'magazyn_falownik_sieciowy', tytul: 'Falownik nie obsługuje magazynu',
      fakt: 'To falownik sieciowy, bez wejścia na baterię.',
      akcja: 'Poproś o falownik hybrydowy albo osobny falownik bateryjny.',
      dlaczego: 'Magazyn w ofercie to bateria bez własnego falownika. Ładuje się przez falownik z wejściem na baterię, więc w tym zestawie nie będzie działać.' })
  } else if (bezBaterii === 'pomaranczowa') {
    flagi.push({ poziom: 'pomaranczowa', kod: 'magazyn_falownik_sieciowy', tytul: 'Magazyn przy falowniku sieciowym',
      akcja: 'Zapytaj, jak magazyn zostanie podłączony i jak będzie sterowany.',
      dlaczego: 'Falownik jest sieciowy, a magazyn ma własny falownik, więc łączy się je po stronie prądu przemiennego. To działa, ale wymaga osobnego podłączenia i sterowania.' })
  }
  if (wejscie?.zasilanieAwaryjne === 'tak' && wynik?.falownik?.backup === 'nie') {
    flagi.push({ poziom: 'pomaranczowa', kod: 'backup_osobny', tytul: 'Zasilanie awaryjne wymaga dodatkowego urządzenia',
      akcja: 'Zapytaj, czy to urządzenie jest w cenie i co będzie zasilane.',
      dlaczego: 'Według danych producenta ten falownik nie ma wbudowanego zasilania awaryjnego.' })
  }
  // Certyfikat NC RfG tylko z wykazu PTPiREE 1.2: od 1.01.2027 operatorzy sieci przyjmują zgłoszenia
  // mikroinstalacji tylko z urządzeniami z wykazu 1.3 (komunikat pięciu OSD z 5.10.2026). Status liczy
  // backend (`certyfikaty.py`) i podaje go tylko, gdy wszyscy kandydaci falownika mają ten sam.
  const cert = wynik?.falownik?.certyfikat
  if (cert && ['tylko_12', 'wygasl'].includes(cert.status)) {
    const do_ = cert.akceptowany_do ? cert.akceptowany_do.split('-').reverse().join('.') : null
    flagi.push(cert.status === 'wygasl'
      ? { poziom: 'pomaranczowa', kod: 'certyfikat_wygasl', tytul: 'Certyfikat falownika nie jest już akceptowany',
        akcja: 'Zapytaj, czy falownik ma certyfikat z wykazu 1.3, zanim podpiszesz umowę.',
        dlaczego: `Certyfikat NC RfG tego falownika jest tylko w wykazie PTPiREE 1.2${do_ ? ` i był akceptowany do ${do_}` : ''}. Bez certyfikatu z wykazu 1.3 operator sieci może nie przyjąć zgłoszenia mikroinstalacji.` }
      : { poziom: 'pomaranczowa', kod: 'certyfikat_12', tytul: 'Falownik ma certyfikat tylko z wykazu 1.2',
        akcja: 'Zapytaj, czy instalacja zostanie zgłoszona do operatora przed 1 stycznia 2027, albo poproś o falownik z wykazu 1.3.',
        dlaczego: `Według komunikatu operatorów sieci z 5 października 2026 zgłoszenia mikroinstalacji składane po 1 stycznia 2027 muszą dotyczyć urządzeń z wykazu PTPiREE w wersji 1.3. Ten falownik jest tylko w wykazie 1.2${do_ ? ` (akceptowany do ${do_})` : ''}.` })
  }
  if (ocenaSerwisu(wynik?.falownik?.marka) === 'nie') {
    flagi.push({ poziom: 'pomaranczowa', kod: 'serwis_falownika', tytul: `${wynik.falownik.marka.producent}: brak serwisu w Polsce`,
      akcja: 'Zapytaj, kto przyjmie reklamację i ile trwa naprawa.',
      dlaczego: 'Marka falownika nie ma w Polsce własnej spółki serwisowej.' })
  }
  if (magazyn && serwisMagazynu(wynik) === 'nie' && wynik?.magazyn?.marka?.producent !== wynik?.falownik?.marka?.producent) {
    flagi.push({ poziom: 'pomaranczowa', kod: 'serwis_magazynu', tytul: `${wynik.magazyn.marka.producent}: brak serwisu w Polsce`,
      akcja: 'Zapytaj, kto przyjmie reklamację i ile trwa naprawa.',
      dlaczego: 'Marka magazynu nie ma w Polsce własnej spółki serwisowej.' })
  }
  return flagi
}

/**
 * Cena daleko od typowego zakresu. Nie przy zakresie niepełnym: gdy katalog nie zna ceny
 * którejś pozycji (np. magazynu), zakres jest zaniżony i „powyżej” byłoby fałszywym alarmem.
 */
const flagiCeny = (werdykt, suma) => {
  if (suma?.niepelny) return []
  if (!werdykt?.procent || werdykt.procent < CENA_DALEKO_PROC) return []
  if (werdykt.typ === 'ponizej') {
    return [{ poziom: 'pomaranczowa', kod: 'cena_nisko', tytul: `Cena o ${werdykt.procent}% poniżej typowego zakresu`,
      akcja: 'Zapytaj, czego nie ma w cenie.',
      dlaczego: 'Sprawdź zwłaszcza zabezpieczenia, zgłoszenie do operatora sieci i montaż magazynu.' }]
  }
  if (werdykt.typ === 'powyzej') {
    // Co najmniej 1,3 × górna granica zakresu to czerwona flaga (decyzja właściciela 2026-10-01).
    return [{ poziom: 'czerwona', kod: 'cena_wysoko', tytul: `Cena o ${werdykt.procent}% powyżej typowego zakresu`,
      akcja: 'Poproś o rozpisanie ceny na pozycje i porównaj z inną ofertą.',
      dlaczego: 'Różnicę mogą tłumaczyć np. dłuższa gwarancja na montaż, trudny dach albo dodatkowe prace, ale przy takiej różnicy warto je zobaczyć czarno na białym.' }]
  }
  return []
}

const KOLEJNOSC = { czerwona: 0, pomaranczowa: 1, info: 2 }

/** Wszystkie flagi oferty, od czerwonych. `raport` z `policzRaport` (wejscie, kwp, werdykt). */
/**
 * Zastrzeżenia z treści oferty („cena od”, „do 10 kWp”, kilka marek do wyboru): pomarańczowe,
 * bo zmieniają sens liczb, z których liczymy raport (prośba właściciela 2026-10-05).
 */
const TRESC_ZASTRZEZEN = {
  'cena:od': { tytul: 'Cena „od”, a nie wycena dla Twojego domu', akcja: 'Poproś o kwotę za całość dla Twojego dachu i zużycia, na piśmie.',
    dlaczego: 'Cena „od” dotyczy zwykle najprostszego wariantu: najtańszego dachu, krótkich tras kabli i bez dodatków. Końcowa kwota bywa wyższa.' },
  'cena:okolo': { tytul: 'Cena przybliżona („około”)', akcja: 'Poproś o dokładną kwotę za całość.',
    dlaczego: 'Raport porównuje kwotę z oferty z typowym zakresem; przy kwocie przybliżonej wynik też jest przybliżony.' },
  'kwp:do': { tytul: 'Moc „do” zamiast konkretnej mocy', akcja: 'Zapytaj, ile modułów i jakiej mocy zamontują.',
    dlaczego: 'Od mocy zależą cena, produkcja i dobór falownika. Górna granica nie mówi, co dostaniesz.' },
  'kwp:zakres': { tytul: 'Moc podana jako zakres', akcja: 'Zapytaj, ile modułów i jakiej mocy zamontują.',
    dlaczego: 'Od mocy zależą cena, produkcja i dobór falownika. Liczymy dla dolnej wartości.' },
  'moduly:do_ustalenia': { tytul: 'Liczba modułów „do ustalenia”', akcja: 'Poproś o liczbę i model modułów przed podpisaniem umowy.',
    dlaczego: 'Bez liczby modułów nie wiadomo, za jaką moc płacisz, i trudno porównać oferty.' },
  'oferta:przykladowa': { tytul: 'Oferta opisuje przykładowy zestaw', akcja: 'Poproś o ofertę z konkretnym sprzętem dla Twojego domu.',
    dlaczego: 'Przykładowy zestaw z ogłoszenia nie uwzględnia Twojego dachu, zużycia ani przyłącza.' },
  'moduly:przyjete': { tytul: 'Moduły przyjęte przez nas, bo oferta ich nie podaje', akcja: 'Poproś o model i liczbę modułów, zanim podpiszesz umowę.',
    dlaczego: 'Bez modelu i liczby modułów cena modułów i dobór falownika w raporcie to szacunek dla typowego modułu ok. 500 W.' },
  'falownik:przyjete': { tytul: 'Falownik przyjęty przez nas, bo oferta go nie podaje', akcja: 'Poproś o markę i model falownika, zanim podpiszesz umowę.',
    dlaczego: 'Bez modelu falownika liczymy cenę typowego falownika o mocy zbliżonej do mocy paneli, dowolnej marki. Od falownika zależą też gwarancja, serwis i zgodność z magazynem.' },
  'falownik:moc_przyjeta': { tytul: 'Moc falownika przyjęta przez nas', akcja: 'Poproś o pełny model falownika, zanim podpiszesz umowę.',
    dlaczego: 'Liczymy cenę falownika tej marki o mocy zbliżonej do mocy paneli. Za słaby falownik obcina produkcję, a od modelu zależy też zgodność z magazynem.' },
  'magazyn:pojemnosci': { tytul: 'Kilka pojemności magazynu do wyboru', akcja: 'Sprawdź, której pojemności dotyczy cena.',
    dlaczego: 'Cena magazynu rośnie z pojemnością. Liczymy dla pierwszej podanej.' },
}
const flagiZastrzezen = (zastrzezenia) => (zastrzezenia || []).map((z) => {
  if (z.rodzaj === 'alternatywy') {
    return { poziom: 'pomaranczowa', kod: `zastrz_${z.pole}_alternatywy`, tytul: `Kilka marek do wyboru: ${(z.marki || []).join(', ')}`,
      akcja: 'Zapytaj, który model trafi do umowy, i poproś o wpisanie go z nazwy.',
      dlaczego: 'Raport liczymy dla pierwszej marki; ceny, gwarancje i serwis innych mogą się różnić.' }
  }
  const t = TRESC_ZASTRZEZEN[`${z.pole}:${z.rodzaj}`]
  return t ? { poziom: 'pomaranczowa', kod: `zastrz_${z.pole}_${z.rodzaj}`, ...t } : null
}).filter(Boolean)

export const flagiOferty = (wynik, raport) => {
  if (!wynik || !raport) return []
  const { wejscie, kwp, werdykt, suma } = raport
  // Jeden problem, jedna flaga: gdy baza zgodności już mówi „nie współpracują”, flaga
  // o falowniku sieciowym niczego nie dodaje; gdy to my wiemy, że falownik nie obsługuje
  // baterii, „brak potwierdzenia pary” byłby mylący.
  const zgodnosc = flagiZgodnosci(wynik)
  const niezgodne = zgodnosc.some((f) => f.kod === 'niezgodne')
  const sprzet = flagiSprzetu(wynik, wejscie).filter((f) => !(niezgodne && f.kod === 'magazyn_falownik_sieciowy'))
  const bezBaterii = sprzet.some((f) => f.kod === 'magazyn_falownik_sieciowy' && f.poziom === 'czerwona')
  // Kolejność w obrębie poziomu: najpierw to, przez co sprzęt w ogóle nie zadziała razem.
  return [
    ...zgodnosc.filter((f) => !(bezBaterii && f.kod === 'zgodnosc_brak')),
    ...sprzet,
    ...flagiModulowMagazynu(wynik, wejscie),
    ...flagiLancuchow(wynik, wejscie),
    ...flagiMocy(wynik, kwp),
    ...flagiCeny(werdykt, suma),
    ...flagiZastrzezen(raport.zastrzezenia),
    ...flagiFormalnosci(kwp),
  ].sort((a, b) => KOLEJNOSC[a.poziom] - KOLEJNOSC[b.poziom])
}

export const maCzerwona = (flagi) => (flagi || []).some((f) => f.poziom === 'czerwona')

/**
 * Pomarańczowe flagi, które trzeba wyjaśnić przed wyborem, bo dotyczą tego, czy sprzęt
 * w ogóle zadziała razem. Werdykt porównania dopisuje je do zdania („najpierw wyjaśnij”).
 */
export const KODY_PILNE = ['niezgodne_czesc', 'zgodnosc_sprzeczna', 'zgodnosc_brak', 'prad_zwarciowy',
  'za_duzo_modulow', 'magazyn_falownik_sieciowy']
export const pilnaFlaga = (flagi) => (flagi || []).find((f) => f.poziom === 'pomaranczowa' && KODY_PILNE.includes(f.kod)) || null

export default flagiOferty
